// src/components/partial-pages/VoicePage/VoiceDevicesModal/DevicePickerModal.tsx

import React, { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  Divider,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControl,
  Select,
  MenuItem,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { BRAND_COLOR, getAppColors } from 'lib/constants'
import type { Device } from './types'
import { languageLabel } from './helpers'

const useStyles = makeStyles({
  checkboxRoot: {
    // unchecked outline color
    color: getAppColors('--color-text-muted'),
    '&$checkboxChecked': {
      // ✅ checked fill color
      color: BRAND_COLOR,
    },
  },
  checkboxChecked: {},
})

type Props = {
  open: boolean
  group: { baseChannelName?: string; language?: string } | null
  pool: Device[]
  alreadyInGroupIds: string[]
  loading?: boolean

  pageLabel?: string
  canPrev?: boolean
  canNext?: boolean
  onPrev?: () => void
  onNext?: () => void

  onClose: () => void
  onAddSelected: (devices: Device[]) => void
}

export function DevicePickerModal({
  open,
  group,
  pool,
  alreadyInGroupIds,
  loading,
  pageLabel,
  canPrev,
  canNext,
  onPrev,
  onNext,
  onClose,
  onAddSelected,
}: Props) {
  const classes = useStyles()

  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<'all' | 'unassigned' | 'assigned'>('all')
  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (!open) return
    setSearch('')
    setFilter('all')
    setSelectedIds({})
  }, [open])

  const alreadySet = useMemo(() => {
    const m: Record<string, boolean> = {}
    for (const id of alreadyInGroupIds || []) m[String(id)] = true
    return m
  }, [alreadyInGroupIds])

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase()
    return (pool || [])
      .filter(d => {
        if (filter === 'unassigned' && d.assignedGroupId) return false
        if (filter === 'assigned' && !d.assignedGroupId) return false

        if (!s) return true
        return (
          String(d.name || '')
            .toLowerCase()
            .includes(s) ||
          String(d.id || '')
            .toLowerCase()
            .includes(s) ||
          String(d.assignedTo || '')
            .toLowerCase()
            .includes(s)
        )
      })
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }))
  }, [pool, search, filter])

  const selectedDevices = useMemo(() => {
    const ids = Object.keys(selectedIds).filter(k => selectedIds[k])
    const idSet: Record<string, boolean> = {}
    ids.forEach(id => (idSet[id] = true))
    return (pool || []).filter(d => idSet[d.id])
  }, [selectedIds, pool])

  function toggleOne(id: string, disabled: boolean) {
    if (disabled) return
    setSelectedIds(prev => ({ ...prev, [id]: !prev[id] }))
  }

  function clearSelected() {
    setSelectedIds({})
  }

  function handleAdd() {
    const toAdd = selectedDevices.filter(d => !alreadySet[d.id])
    onAddSelected(toAdd)
    onClose()
  }

  const title = group ? `Add devices — ${group.baseChannelName} (${languageLabel(group.language)})` : 'Add devices'

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle style={{ fontFamily: 'Montserrat', fontWeight: 900 }}>{title}</DialogTitle>

      <DialogContent dividers style={{ paddingTop: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: 12, alignItems: 'center' }}>
          <TextField
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search devices by name or ID…"
            variant="outlined"
            size="small"
            fullWidth
            disabled={Boolean(loading)}
          />

          <FormControl variant="outlined" size="small" disabled={Boolean(loading)}>
            <Select value={filter} onChange={e => setFilter(e.target.value as any)}>
              <MenuItem value="all">{'All devices'}</MenuItem>
              <MenuItem value="unassigned">{'Unassigned'}</MenuItem>
              <MenuItem value="assigned">{'Assigned'}</MenuItem>
            </Select>
          </FormControl>
        </div>

        <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip
            size="small"
            label={loading ? 'Loading…' : `Results: ${filtered.length}`}
            style={{ background: getAppColors('--color-surface-2') }}
          />
          <Chip
            size="small"
            label={`Selected: ${selectedDevices.length}`}
            style={{
              background: selectedDevices.length ? getAppColors('--color-gray-4') : getAppColors('--color-surface-2'),
              fontWeight: 800,
            }}
          />

          {pageLabel && (
            <Chip
              size="small"
              label={pageLabel}
              style={{ background: getAppColors('--color-surface-2'), fontWeight: 800 }}
            />
          )}

          {(canPrev || canNext) && (
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
              <Button
                onClick={onPrev}
                disabled={!canPrev || Boolean(loading)}
                size="small"
                style={{ textTransform: 'none', fontWeight: 800 }}
              >
                Prev
              </Button>
              <Button
                onClick={onNext}
                disabled={!canNext || Boolean(loading)}
                size="small"
                style={{ textTransform: 'none', fontWeight: 800 }}
              >
                Next
              </Button>
            </div>
          )}

          {selectedDevices.length > 0 && (
            <Button
              onClick={clearSelected}
              size="small"
              style={{ textTransform: 'none', fontWeight: 800, marginLeft: pageLabel ? 0 : 'auto' }}
              disabled={Boolean(loading)}
            >
              Clear selected
            </Button>
          )}
        </div>

        <Divider style={{ marginTop: 12, marginBottom: 10 }} />

        <div style={{ border: `1px solid ${getAppColors('--color-gray-4')}`, borderRadius: 10, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow style={{ background: getAppColors('--color-surface-2') }}>
                <TableCell padding="checkbox" />
                <TableCell style={{ fontWeight: 900 }}>{'Name'}</TableCell>
                <TableCell style={{ fontWeight: 900 }}>{'ID'}</TableCell>
                <TableCell style={{ fontWeight: 900 }}>{'Assigned'}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filtered.map(d => {
                const alreadyInThisGroup = Boolean(alreadySet[d.id])
                const disabled = alreadyInThisGroup || Boolean(loading)
                const checked = Boolean(selectedIds[d.id])

                return (
                  <TableRow key={d.id} hover style={{ opacity: disabled ? 0.55 : 1 }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggleOne(d.id, disabled)}
                        classes={{ root: classes.checkboxRoot, checked: classes.checkboxChecked }}
                      />
                    </TableCell>

                    <TableCell style={{ fontWeight: 800 }}>{d.name}</TableCell>

                    <TableCell
                      style={{ color: getAppColors('--color-secondary'), fontFamily: 'monospace', fontSize: 12 }}
                    >
                      {d.id}
                    </TableCell>

                    <TableCell style={{ color: getAppColors('--color-secondary'), fontSize: 13 }}>
                      {alreadyInThisGroup ? (
                        <span style={{ fontWeight: 900 }}>{'Already added'}</span>
                      ) : d.assignedTo ? (
                        d.assignedTo
                      ) : (
                        'Unassigned'
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}

              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} style={{ padding: 16, color: getAppColors('--color-secondary') }}>
                    {loading ? 'Loading devices…' : 'No devices match your search/filters.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </DialogContent>

      <DialogActions style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ color: getAppColors('--color-secondary'), fontWeight: 800 }}>
          {selectedDevices.length ? `${selectedDevices.length} selected` : 'No devices selected'}
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            style={{
              borderColor: getAppColors('--color-gray-1'),
              color: getAppColors('--color-text'),
              fontWeight: 900,
              textTransform: 'none',
              borderRadius: 18,
              height: 34,
              paddingLeft: 14,
              paddingRight: 14,
            }}
            disabled={Boolean(loading)}
          >
            Cancel
          </Button>

          <Button
            onClick={handleAdd}
            variant="contained"
            disabled={selectedDevices.length === 0 || Boolean(loading)}
            style={{
              backgroundColor: selectedDevices.length && !loading ? BRAND_COLOR : undefined,
              color: selectedDevices.length && !loading ? '#fff' : undefined,
              fontWeight: 900,
              textTransform: 'none',
              borderRadius: 18,
              height: 34,
              minWidth: 170,
              paddingLeft: 16,
              paddingRight: 16,
            }}
          >
            {selectedDevices.length ? `Add selected (${selectedDevices.length})` : 'Add selected'}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  )
}
